import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { GlobalSearchDocument, type GlobalSearchInput } from "@/graphql/generated/operations";

// The shared ranked search and offset paging. Person cards hydrate the thin results;
// administrative identity search selects its authorized login details in the same query.
export function useGlobalSearch({
  input, pageSize = 24, skip = false, includeLoginAssignment = false,
}: {
  input: GlobalSearchInput;
  pageSize?: number;
  skip?: boolean;
  includeLoginAssignment?: boolean;
}) {
  const searchInput = useMemo(() => ({ ...input, skip: 0, take: pageSize }), [input, pageSize]);
  const variables = useMemo(() => ({
    input: searchInput,
    ...(includeLoginAssignment ? { includeLoginAssignment: true } : {}),
  }), [searchInput, includeLoginAssignment]);
  const search = useQuery(GlobalSearchDocument, { variables, skip });
  const results = useMemo(() => search.data?.globalSearch.results ?? [], [search.data]);
  const hasMore = search.data?.globalSearch.hasNextPage ?? false;
  const requestKey = JSON.stringify(variables);
  const [paging, setPaging] = useState({ key: requestKey, loading: false, failed: false });
  const loadingMore = paging.key === requestKey && paging.loading;
  const loadMoreFailed = paging.key === requestKey && paging.failed;
  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore) return;
    setPaging({ key: requestKey, loading: true, failed: false });
    search.fetchMore({
      variables: { ...variables, input: { ...searchInput, skip: results.length } },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          ...prev,
          globalSearch: {
            ...fetchMoreResult.globalSearch,
            results: [...(prev.globalSearch?.results ?? []), ...(fetchMoreResult.globalSearch?.results ?? [])],
          },
        };
      },
    }).catch(() => setPaging((current) => current.key === requestKey ? { ...current, failed: true } : current))
      .finally(() => setPaging((current) => current.key === requestKey ? { ...current, loading: false } : current));
  }, [hasMore, loadingMore, search, variables, searchInput, results.length, requestKey]);
  return { results, loading: search.loading, error: search.error, hasMore, loadingMore,
    loadMoreFailed, loadMore, refetch: () => search.refetch() };
}
